<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, useTemplateRef, watch } from 'vue'
import Cookies from 'js-cookie'
import plural from 'plural-ru'
import ChargeIndicator from '../ChargeIndicator.vue'
import AudioPlayerV2 from '../AudioPlayerV2.vue'
import NightPlayer from './Audio/NightPlayer.vue'
import NightSwitch from './Audio/NightSwitch.vue'
import Spider from './Audio/Spider.vue'
import { httpPost } from '@/lib/http'
import { AudioActivityTracker } from '@/helpers/trackers'
import type { Student2Context } from '@/types/student2'
import { useStaticAsset } from '@/composables/useStaticAsset'

const asset = useStaticAsset()

const props = defineProps<{ context: Student2Context }>()
const emit = defineEmits<{
  (e: 'update:discharge', value: number): void
  (e: 'update:subcharge', value: number): void
  (e: 'add-subcharge', value: number): void
  (e: 'add-star'): void
}>()

const studentId = Number(window.location.pathname.split('/').slice(-2)[0])
const unitId = Number(window.location.pathname.split('/').slice(-1)[0])

const tailsAudioOrder = ref<string[]>([])
const currentAudioOrder = ref<string[]>([])
const playing = ref(false)

const nightModeAvailable = ref(false)
const nightModeActive = ref(false)
const nightStarsCount = ref(props.context.smartbook?.awarded_night_stars_count ?? 0)

const pageRef = useTemplateRef<HTMLElement>('pageRef')
const spider = useTemplateRef('spider')
const nightPlayer = useTemplateRef('nightPlayer')

const materialTrackers: Record<string, AudioActivityTracker> = {}
const subchargeIntervals: Record<string, AudioActivityTracker> = {}

let timeIntval: ReturnType<typeof setInterval> | null = null
let resizeObserver: ResizeObserver | null = null

const pluralize = (value = 0, one: string, two: string, five: string) => `${value} ${plural(value, one, two, five)}`

const activeUnitIndex = computed(() => props.context.course_part.units.findIndex(u => u.id === unitId))
const canSubcharge = computed(() => props.context.smartbook?.current_unit_charges_battery)

const showChargeIndicator = computed(() => {
  if (!props.context.smartbook) return false
  if (props.context.student.free_mode) return false
  if (activeUnitIndex.value === 0) return true
  return props.context.smartbook.purchased
})

const tailSongKey = (tail: Student2Context['student']['tails'][number]) => {
  const song = tail.custom_data?.song
  if (!song) return null
  return typeof song === 'string' ? song : song.key
}

const tailSongName = (tail: Student2Context['student']['tails'][number]) => {
  const song = tail.custom_data?.song
  if (!song) return null
  return typeof song === 'string' ? song : song.name
}

const materials = computed(() => {
  if (!props.context.audio_lessons?.length) return { current: [], tails: [] }

  return {
    current: currentAudioOrder.value.flatMap(name => {
      const sameMaterials = props.context.audio_lessons.filter(m => m.name === name)
      const firstMaterial = sameMaterials[0]
      if (!firstMaterial) return []
      return {
        name,
        id: firstMaterial.id,
        rus: sameMaterials.find(m => m.language === 'rus'),
        eng: sameMaterials.find(m => m.language === 'eng'),
        ukr: sameMaterials.find(m => m.language === 'ukr'),
        tat: sameMaterials.find(m => m.language === 'tat')
      }
    }),

    tails: tailsAudioOrder.value.flatMap(key => {
      const tail = props.context.student.tails.find(e => tailSongKey(e) === key)
      const songName = tail ? tailSongName(tail) : null
      if (!tail || !songName || tail.unit_id == null) return []

      return {
        id: key,
        name: songName,
        rus: {
          id: key,
          name: songName,
          url: `/student/get_song_file/${studentId}/${tail.unit_id}/${key}/plus`
        }
      }
    })
  }
})

const onAudioPlay = (materialId: string | number, reason: 'tail' | 'current') => {
  playing.value = true

  const trackerKey = `${materialId}-${reason}`
  if (materialTrackers[trackerKey]) return materialTrackers[trackerKey].onAudioPlayed()

  const audioElement = document.querySelector(
    `div[data-material-id="${materialId}"] > audio`
  ) as HTMLAudioElement | null
  if (!audioElement) return

  materialTrackers[trackerKey] = new AudioActivityTracker(audioElement, async (ms: number) => {
    if (reason === 'tail') {
      const form = new FormData()
      form.append('csrfmiddlewaretoken', Cookies.get('csrftoken') ?? '')
      form.append('student_id', String(studentId))
      form.append('song_key', String(materialId))
      form.append('milliseconds', String(ms))

      navigator.sendBeacon('/student/track_song_activity', form)
      return
    }

    try {
      const resp = await httpPost<{ seconds_to_discharge?: number; subcharge_percent?: number }>(
        '/student/track_material_activity',
        {
          student_id: studentId,
          material_id: audioElement.dataset.activeMaterialId,
          course_id: props.context.course_part.course_id,
          milliseconds: ms
        }
      )

      if (resp.seconds_to_discharge) emit('update:discharge', resp.seconds_to_discharge)
      if (resp.subcharge_percent) emit('update:subcharge', resp.subcharge_percent)
    } catch {
      // noop
    }
  })

  if (reason === 'current' && canSubcharge.value) {
    subchargeIntervals[trackerKey] = new AudioActivityTracker(
      audioElement,
      (ms: number) => {
        const percentToAdd = ms / (audioElement.duration * 1000)
        emit('add-subcharge', percentToAdd * 100)
      },
      5000
    )

    subchargeIntervals[trackerKey].onAudioPlayed()
  }

  materialTrackers[trackerKey].onAudioPlayed()
}

const openNightPlayer = () => {
  const firstEngMaterial = props.context.audio_lessons.find(e => e.language === 'eng')
  if (!firstEngMaterial) return
  nightPlayer.value?.$open(firstEngMaterial.id)
}

const checkNightPlayerAvailable = () => {
  if (sessionStorage.overrideNightMode) {
    nightModeAvailable.value = true
    return
  }

  const now = new Date()
  nightModeAvailable.value = now.getHours() >= 20
}

watch(
  () => props.context,
  () => {
    currentAudioOrder.value = [...new Set(props.context.audio_lessons.map(m => m.name))]
    tailsAudioOrder.value = props.context.student.tails.map(e => tailSongKey(e)).filter((key): key is string => !!key)
  },
  { immediate: true }
)

const onPageScroll = () => {
  spider.value?.$onPageChange()
}

const msUntilNextMinute = 60000 - (Date.now() % 60000)
setTimeout(() => {
  timeIntval = setInterval(() => {
    checkNightPlayerAvailable()
  }, 60000)
}, msUntilNextMinute + 200)

checkNightPlayerAvailable()

onMounted(() => {
  pageRef.value?.addEventListener('scroll', onPageScroll)

  resizeObserver = new ResizeObserver(() => {
    spider.value?.$onPageChange()
  })

  if (pageRef.value) resizeObserver.observe(pageRef.value)
})

onBeforeUnmount(() => {
  if (timeIntval) clearInterval(timeIntval)
  resizeObserver?.disconnect()
  pageRef.value?.removeEventListener('scroll', onPageScroll)
})
</script>

<template>
  <section ref="pageRef" class="student-cabinet__page student-playlist-page student-playlist-page_orange">
    <Spider ref="spider" />

    <div>
      <NightPlayer
        ref="nightPlayer"
        :context="context"
        :can-subcharge="canSubcharge"
        @close="
          () => {
            nightModeActive = false
          }
        "
        @add-star="
          () => {
            nightStarsCount++
            $emit('add-star')
          }
        "
        @update:discharge="$emit('update:discharge', $event)"
        @update:subcharge="$emit('update:subcharge', $event)"
        @add-subcharge="$emit('add-subcharge', $event)"
      />
    </div>

    <div v-if="showChargeIndicator" class="d-flex align-center my-2">
      <ChargeIndicator
        class="flex-spacer"
        :subcharge-disabled="!canSubcharge"
        :context="context"
        :charging-now="playing && canSubcharge"
      />
    </div>

    <!-- FIXME: temporarily hide stars on I Can Sing -->
    <div
      v-if="context.course_part.codename === 'icanspeak'"
      class="student-playlist-page__night-mode"
      :class="{ 'student-playlist-page__night-mode_available': nightModeAvailable }"
    >
      <span class="flex-spacer">Ночной режим (после 20:00)</span>
      <div ref="star">
        <v-icon v-if="nightStarsCount" key="1" size="32">mdi-star</v-icon>
        <v-icon v-else key="0" size="32">mdi-star-outline</v-icon>
      </div>

      <NightSwitch
        v-model="nightModeActive"
        class="ml-2"
        :disabled="!nightModeAvailable"
        @update:model-value="openNightPlayer"
      />
    </div>

    <ul class="student-materials-block__list mb-4">
      <li v-for="material in materials.current" :key="material.id" class="list-animation-item">
        <p class="text-body-bold mb-2" v-text="material.name" />
        <AudioPlayerV2
          :student-id="studentId"
          :material="material"
          :data-material-id="material.id"
          @play="onAudioPlay(material.id, 'current')"
          @pause="
            () => {
              playing = false
            }
          "
        />
      </li>
    </ul>

    <section class="student-materials-block">
      <header class="student-materials-block__header">
        <v-icon size="40" class="orange-text">mdi-playlist-music</v-icon>

        <div class="d-flex flex-column flex-spacer mx-2">
          <h2 class="text-title-bold">Хвосты</h2>
          <p v-if="tailsAudioOrder.length" class="gray-text text-caption">
            {{ pluralize(tailsAudioOrder.length, 'аудиоурок', 'аудиоурока', 'аудиоуроков') }}
          </p>
        </div>

        <img class="student-materials-block__header-image" :src="asset('img/student-playlist-hero.png')" height="260" />
      </header>

      <ul v-if="!materials.tails.length" class="student-materials-block__list">
        <li class="d-flex align-center text-title-bold green-text px-d-3 pa-1">
          <v-icon size="48" class="mr-2">like-green</v-icon>
          Молодец, хвостов по песням нет!
        </li>
      </ul>

      <ul v-else class="student-materials-block__list">
        <li v-for="material in materials.tails" :key="material.id" class="list-animation-item mx-2 mt-2">
          <p class="text-body-bold mb-2" v-text="material.name" />
          <AudioPlayerV2
            :student-id="studentId"
            :material="material"
            :data-material-id="material.id"
            @play="onAudioPlay(material.id, 'tail')"
          />
        </li>
      </ul>
    </section>
  </section>
</template>

<style lang="scss">
@use 'playlist-styles.scss' as *;

.student-playlist-page__night-mode {
  background: $lightenGray;
  border: $lightGray 1px solid;
  border-radius: 8px;
  color: $darkGray;

  margin: 16px 0;
  display: flex;
  align-items: center;
  padding: 8px;

  .v-icon {
    color: $gray;
  }

  &_available {
    color: black;

    .v-icon {
      color: $orange;
    }

    border: 1px solid $darkBlue;
  }
}
</style>
